import * as React from "react";
import Box from "@mui/material/Box";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "../../../../app/providers/AuthProvider";
import SelectContent from "./SelectContent";

import homeIcon from "../../../../shared/assets/icons/sidebar/home.svg";
import hubIcon from "../../../../shared/assets/icons/sidebar/hub.svg";
import alignLeftFillIcon from "../../../../shared/assets/icons/sidebar/align-left-fill.svg";
import chatIcon from "../../../../shared/assets/icons/sidebar/chat.svg";
import sendSymbolIcon from "../../../../shared/assets/icons/sidebar/send-symbol.svg";
import categoryIcon from "../../../../shared/assets/icons/sidebar/category.svg";
import moonIcon from "../../../../shared/assets/icons/sidebar/moon.svg";
import logoutIcon from "../../../../shared/assets/icons/sidebar/logout.svg";
import * as colorTokens from "../../../../shared/theme/colors";
import {
  getStoredSidebarDarkMode,
  setStoredSidebarDarkMode,
  SIDEBAR_COLOR_MODE,
} from "../../useAppLayoutAppearance";

export const EXPANDED_SIDEBAR_WIDTH = 299;
export const COLLAPSED_SIDEBAR_WIDTH = 103;
export const SIDEBAR_CONTENT_GAP = 44;
export const FIGMA_DASHBOARD_WIDTH = 1728;
export const FIGMA_SIDEBAR_HEIGHT = 1068;

const darkShadow = "0px 4px 18px rgba(0, 0, 0, 0.16)";
const navFont = "'Poppins', 'Inter', 'Roboto', Helvetica, Arial, sans-serif";

const SIDEBAR_SCALE = {
  navContent: 0.92,
};

const SIDEBAR_DIMENSIONS = {
  brandSpacerHeight: 32.4,
  brandSpacerWidth: 49.68,
  collapsedItemWidth: 48,
  iconPadding: 8,
  iconSize: 32,
  itemHeight: 48,
  itemWidth: 277,
  itemX: 8,
  labelLine: 24,
  labelPadding: 8,
  labelSize: 16,
  navGap: 16,
  sectionGap: 48,
  bottomGap: 24,
  x: 24,
  y: 32,
  toggleIconSize: 19,
  toggleSize: 28,
  toggleTop: 28,
};

const lightPalette = {
  activeBg: "#dfe7f3",
  activeText: "#102033",
  bg: "hsl(228deg 33.33% 97.06%)",
  badgeText: "#1e252b",
  hover: "#e7edf7",
  logoutBg: "#526b80",
  logoutHover: "#455d72",
  logoutText: "#ffffff",
  mutedText: "#2f465a",
  shadow: "none",
  toggleBg: "rgba(67, 88, 108, 0.16)",
  toggleDot: colorTokens.purple[400],
  toggleDotActive: colorTokens.purple[400],
  toggleText: "#102033",
};

const darkPalette = {
  activeBg: "#24394b",
  activeText: "#ffffff",
  bg: "#082033",
  badgeText: "#dcecf8",
  hover: "rgba(233, 245, 254, 0.10)",
  logoutBg: "#667a8a",
  logoutHover: "#7890a2",
  logoutText: "#ffffff",
  mutedText: "#9fb2c2",
  shadow: darkShadow,
  toggleBg: "#24394b",
  toggleDot: "#667a8a",
  toggleDotActive: colorTokens.purple[600],
  toggleText: "#e9f5fe",
};

const navItems = [
  { id: "home", label: "Dashboard", href: "/painel", icon: homeIcon, iconScale: 0.94 },
  { id: "media", label: "Canais", href: "/canais", icon: hubIcon },
  { id: "pages", label: "Listagens", href: "/listagens", icon: alignLeftFillIcon },
  { id: "comments", label: "Modelos", href: "/modelos", icon: chatIcon, iconScale: 0.96 },
  { id: "posts", label: "Campanhas", href: "/campanhas", icon: sendSymbolIcon, iconScale: 0.96 },
  { id: "appearance", label: "Integrações", href: "/integrações", icon: categoryIcon, iconScale: 0.94 },
];

function getActiveItemId(pathname) {
  const decodedPathname = decodeURIComponent(pathname);
  const matches = navItems
    .filter((item) => item.href && decodedPathname.startsWith(item.href))
    .sort((first, second) => second.href.length - first.href.length);

  return matches[0]?.id;
}

function IconImage({ alt = "", scale = 1, src }) {
  return (
    <Box
      aria-label={alt || undefined}
      sx={{
        backgroundColor: "currentColor",
        display: "block",
        height: "var(--sidebar-icon-size, 32px)",
        mask: `url(${src}) center / contain no-repeat`,
        transform: `scale(${scale})`,
        transformOrigin: "center",
        WebkitMask: `url(${src}) center / contain no-repeat`,
        width: "var(--sidebar-icon-size, 32px)",
      }}
    />
  );
}

function ToggleExpandButton({ expanded, onToggleExpanded, sidebarDark = true }) {
  if (!onToggleExpanded) return null;

  const Icon = expanded ? ChevronLeftRoundedIcon : ChevronRightRoundedIcon;

  return (
    <Box
      component="button"
      type="button"
      aria-label={expanded ? "Recolher menu" : "Expandir menu"}
      onClick={onToggleExpanded}
      sx={{
        alignItems: "center",
        backgroundColor: "var(--sidebar-bg)",
        border: sidebarDark
          ? `1px solid ${colorTokens.purple[600]}`
          : "1px solid rgba(93, 114, 133, 0.18)",
        borderRadius: "999px",
        boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.10)",
        color: "var(--sidebar-muted-text)",
        cursor: "pointer",
        display: "flex",
        height: "var(--sidebar-toggle-size, 28px)",
        justifyContent: "center",
        p: 0,
        position: "absolute",
        right: "calc(var(--sidebar-toggle-size, 28px) / -2)",
        top: "var(--sidebar-toggle-top, 28px)",
        width: "var(--sidebar-toggle-size, 28px)",
        zIndex: 2,
        "&:hover": { color: "var(--sidebar-active-text)" },
      }}
    >
      <Icon sx={{ fontSize: "var(--sidebar-toggle-icon-size, 19px)" }} />
    </Box>
  );
}

function NavButton({
  active = false,
  badge,
  expanded,
  labelVisible = expanded,
  label,
  onClick,
  iconScale = 1,
  src,
  variant = "default",
}) {
  const isLogout = variant === "logout";
  const backgroundColor = isLogout
    ? "var(--sidebar-logout-bg)"
    : active
      ? "var(--sidebar-active-bg)"
      : "transparent";
  const color = isLogout
    ? "var(--sidebar-logout-text)"
    : active
      ? "var(--sidebar-active-text)"
      : "var(--sidebar-muted-text)";

  return (
    <Box
      component="button"
      type="button"
      aria-label={label}
      onClick={onClick}
      sx={{
        alignItems: "center",
        backgroundColor,
        border: 0,
        borderRadius: "4px",
        boxSizing: "border-box",
        color,
        cursor: "pointer",
        display: "flex",
        justifyContent: "flex-start",
        m: 0,
        minHeight: "var(--sidebar-item-height, 48px)",
        overflow: "hidden",
        p: 0,
        px: expanded ? "var(--sidebar-item-x, 8px)" : 0,
        transition: "background-color 140ms ease, width 220ms ease",
        width: expanded
          ? "var(--sidebar-item-width, 277px)"
          : "var(--sidebar-collapsed-item-width, 48px)",
        "&:hover": {
          backgroundColor: isLogout
            ? "var(--sidebar-logout-hover)"
            : active
              ? "var(--sidebar-active-bg)"
              : "var(--sidebar-hover)",
        },
        "&:focus-visible": {
          outline: `2px solid ${colorTokens.purple[500]}`,
          outlineOffset: 2,
        },
      }}
    >
      <Box
        sx={{
          alignItems: "center",
          display: "flex",
          justifyContent: "center",
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            justifyContent: "center",
            p: "var(--sidebar-icon-padding, 8px)",
          }}
        >
          <IconImage scale={iconScale} src={src} />
        </Box>
        <Box
          component="span"
          sx={{
            color: "inherit",
            display: "block",
            fontFamily: navFont,
            fontSize: "var(--sidebar-label-size, 16px)",
            fontWeight: active || isLogout ? 600 : 400,
            letterSpacing: "0.16px",
            lineHeight: "var(--sidebar-label-line, 24px)",
            maxWidth: expanded ? "180px" : 0,
            opacity: labelVisible && expanded ? 1 : 0,
            overflow: "hidden",
            p: expanded ? "var(--sidebar-label-padding, 8px)" : 0,
            textAlign: "left",
            textOverflow: "ellipsis",
            transition: "opacity 90ms ease, max-width 220ms ease, padding 220ms ease",
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </Box>
      </Box>

      {expanded && badge ? (
        <Box
          component="span"
          sx={{
            alignItems: "center",
            backgroundColor: "var(--sidebar-active-bg)",
            borderRadius: "4px",
            color: "var(--sidebar-badge-text)",
            display: "flex",
            fontFamily: navFont,
            fontSize: 13,
            height: 24,
            justifyContent: "center",
            lineHeight: "29px",
            width: 24,
          }}
        >
          {badge}
        </Box>
      ) : null}
    </Box>
  );
}

function DarkModeRow({
  expanded,
  hidden = false,
  labelVisible = expanded,
  onToggleDarkMode,
  sidebarDark,
}) {
  return (
    <Box
      component="button"
      type="button"
      aria-label="Dark Mode"
      onClick={onToggleDarkMode}
      sx={{
        alignItems: "center",
        backgroundColor: "transparent",
        border: 0,
        borderRadius: "4px",
        boxSizing: "border-box",
        color: "var(--sidebar-muted-text)",
        cursor: "pointer",
        display: "flex",
        justifyContent: "flex-start",
        m: 0,
        minHeight: "var(--sidebar-item-height, 48px)",
        overflow: "hidden",
        p: 0,
        px: expanded ? "var(--sidebar-item-x, 8px)" : 0,
        transition: "background-color 140ms ease, width 220ms ease",
        visibility: hidden ? "hidden" : "visible",
        width: expanded
          ? "var(--sidebar-item-width, 277px)"
          : "var(--sidebar-collapsed-item-width, 48px)",
        "&:hover": { backgroundColor: "var(--sidebar-hover)" },
      }}
    >
      <Box sx={{ alignItems: "center", display: "flex", justifyContent: "center" }}>
        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            justifyContent: "center",
            p: "var(--sidebar-icon-padding, 8px)",
          }}
        >
          <IconImage src={moonIcon} />
        </Box>
        <Box
          component="span"
          sx={{
            color: "var(--sidebar-muted-text)",
            fontFamily: navFont,
            fontSize: "var(--sidebar-label-size, 16px)",
            fontWeight: 400,
            letterSpacing: "0.16px",
            lineHeight: "var(--sidebar-label-line, 24px)",
            maxWidth: expanded ? "120px" : 0,
            opacity: labelVisible && expanded ? 1 : 0,
            overflow: "hidden",
            p: expanded ? "var(--sidebar-label-padding, 8px)" : 0,
            transition: "opacity 90ms ease, max-width 220ms ease, padding 220ms ease",
            whiteSpace: "nowrap",
          }}
        >
          Dark Mode
        </Box>
      </Box>
      <Box
        sx={{
          alignItems: "center",
          backgroundColor: "var(--sidebar-toggle-bg)",
          borderRadius: "100px",
          display: "block",
          flexShrink: 0,
          height: expanded ? "24px" : 0,
          justifyContent: "center",
          ml: expanded ? "auto" : 0,
          maxWidth: expanded ? "62px" : 0,
          opacity: labelVisible && expanded ? 1 : 0,
          overflow: "hidden",
          position: "relative",
          transition: "opacity 90ms ease, max-width 220ms ease, height 220ms ease",
          width: expanded ? "62px" : 0,
        }}
      >
        <Box
          aria-hidden="true"
          sx={{
            backgroundColor: sidebarDark
              ? "var(--sidebar-toggle-dot-active)"
              : "var(--sidebar-toggle-dot)",
            borderRadius: "999px",
            boxShadow: sidebarDark
              ? "0 0 0 2px rgba(129, 66, 252, 0.14)"
              : "none",
            height: 18,
            left: sidebarDark ? "40px" : "4px",
            position: "absolute",
            top: "3px",
            transition: "left 180ms ease, background-color 140ms ease, box-shadow 140ms ease",
            width: 18,
          }}
        />
        <Box
          component="span"
          sx={{
            color: "var(--sidebar-toggle-text)",
            fontFamily: navFont,
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: "0.26px",
            left: sidebarDark ? "10px" : "30px",
            lineHeight: "24px",
            opacity: 0.72,
            position: "absolute",
            top: 0,
            transition: "left 180ms ease",
            whiteSpace: "nowrap",
          }}
        >
          {sidebarDark ? "ON" : "OFF"}
        </Box>
      </Box>
    </Box>
  );
}

export default function MenuContent({
  expanded = false,
  onItemClick,
  onToggleExpanded,
  scale = 1,
}) {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const activeItemId = getActiveItemId(pathname);
  const innerScale = scale * SIDEBAR_SCALE.navContent;
  const [sidebarDark, setSidebarDark] = React.useState(getStoredSidebarDarkMode);
  const [labelVisible, setLabelVisible] = React.useState(expanded);
  const palette = sidebarDark ? darkPalette : lightPalette;
  const sidebarShadow = sidebarDark ? darkShadow : "none";
  const selectVisible = expanded && labelVisible;

  React.useEffect(() => {
    if (expanded) {
      const showLabelsTimer = window.setTimeout(() => {
        setLabelVisible(true);
      }, 80);

      return () => window.clearTimeout(showLabelsTimer);
    }

    setLabelVisible(false);
    return undefined;
  }, [expanded]);

  function handleNav(item) {
    if (item.externalHref) {
      window.open(item.externalHref, "_blank", "noopener,noreferrer");
      onItemClick?.();
      return;
    }

    if (item.href) {
      navigate(item.href);
      onItemClick?.();
    }
  }

  async function handleLogout() {
    await logout();
    onItemClick?.();
  }

  function handleToggleSidebarDarkMode() {
    if (!SIDEBAR_COLOR_MODE.toggleEnabled) return;
    setSidebarDark((current) => {
      const nextValue = !current;

      setStoredSidebarDarkMode(nextValue);

      return nextValue;
    });
  }

  return (
    <Box
      data-name="Side Nav"
      sx={{
        alignItems: "flex-start",
        backgroundColor: "var(--sidebar-bg)",
        borderRadius: 0,
        boxShadow: sidebarShadow,
        boxSizing: "border-box",
        "--sidebar-active-bg": palette.activeBg,
        "--sidebar-active-text": palette.activeText,
        "--sidebar-badge-text": palette.badgeText,
        "--sidebar-bg": palette.bg,
        "--sidebar-hover": palette.hover,
        "--sidebar-logout-bg": palette.logoutBg,
        "--sidebar-logout-hover": palette.logoutHover,
        "--sidebar-logout-text": palette.logoutText,
        "--sidebar-muted-text": palette.mutedText,
        "--sidebar-brand-compact-size": `${SIDEBAR_DIMENSIONS.brandSpacerHeight * scale}px`,
        "--sidebar-brand-symbol-w": `${SIDEBAR_DIMENSIONS.brandSpacerWidth * scale}px`,
        "--sidebar-icon-padding": `${SIDEBAR_DIMENSIONS.iconPadding * innerScale}px`,
        "--sidebar-icon-size": `${SIDEBAR_DIMENSIONS.iconSize * innerScale}px`,
        "--sidebar-collapsed-item-width": `${SIDEBAR_DIMENSIONS.collapsedItemWidth * innerScale}px`,
        "--sidebar-item-height": `${SIDEBAR_DIMENSIONS.itemHeight * innerScale}px`,
        "--sidebar-item-width": `${SIDEBAR_DIMENSIONS.itemWidth * innerScale}px`,
        "--sidebar-item-x": `${SIDEBAR_DIMENSIONS.itemX * innerScale}px`,
        "--sidebar-label-line": `${SIDEBAR_DIMENSIONS.labelLine * innerScale}px`,
        "--sidebar-label-padding": `${SIDEBAR_DIMENSIONS.labelPadding * innerScale}px`,
        "--sidebar-label-size": `${SIDEBAR_DIMENSIONS.labelSize * innerScale}px`,
        "--sidebar-nav-gap": `${SIDEBAR_DIMENSIONS.navGap * innerScale}px`,
        "--sidebar-section-gap": `${SIDEBAR_DIMENSIONS.sectionGap * scale}px`,
        "--sidebar-bottom-gap": `${SIDEBAR_DIMENSIONS.bottomGap * scale}px`,
        "--sidebar-x": `${SIDEBAR_DIMENSIONS.x * scale}px`,
        "--sidebar-y": `${SIDEBAR_DIMENSIONS.y * scale}px`,
        "--sidebar-toggle-icon-size": `${SIDEBAR_DIMENSIONS.toggleIconSize * scale}px`,
        "--sidebar-toggle-size": `${SIDEBAR_DIMENSIONS.toggleSize * scale}px`,
        "--sidebar-toggle-top": `${SIDEBAR_DIMENSIONS.toggleTop * scale}px`,
        "--sidebar-toggle-bg": palette.toggleBg,
        "--sidebar-toggle-dot": palette.toggleDot,
        "--sidebar-toggle-dot-active": palette.toggleDotActive,
        "--sidebar-toggle-text": palette.toggleText,
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        height: "100%",
        justifyContent: "space-between",
        minHeight: 0,
        px: "var(--sidebar-x)",
        py: "var(--sidebar-y)",
        position: "relative",
        transition: "width 220ms ease, border-radius 220ms ease, box-shadow 220ms ease",
        width: expanded ? EXPANDED_SIDEBAR_WIDTH * scale : COLLAPSED_SIDEBAR_WIDTH * scale,
        "@media (max-height: 820px)": {
          "--sidebar-section-gap": "30px",
          "--sidebar-bottom-gap": "18px",
          "--sidebar-nav-gap": "10px",
          "--sidebar-y": "24px",
        },
        "@media (max-height: 680px)": {
          "--sidebar-icon-padding": "6px",
          "--sidebar-icon-size": "28px",
          "--sidebar-item-height": "40px",
          "--sidebar-label-line": "21px",
          "--sidebar-label-padding": "6px",
          "--sidebar-label-size": "14px",
          "--sidebar-section-gap": "18px",
          "--sidebar-bottom-gap": "12px",
          "--sidebar-nav-gap": "6px",
          "--sidebar-y": "18px",
        },
        "@media (max-height: 560px)": {
          "--sidebar-section-gap": "12px",
          "--sidebar-bottom-gap": "8px",
          "--sidebar-nav-gap": "4px",
          "--sidebar-y": "14px",
        },
      }}
    >
      <ToggleExpandButton
        expanded={expanded}
        onToggleExpanded={onToggleExpanded}
        sidebarDark={sidebarDark}
      />

      <Box
        data-name="Brand + Nav List"
        sx={{
          alignItems: "flex-start",
          display: "flex",
          flexDirection: "column",
          flex: "1 1 auto",
          gap: "var(--sidebar-section-gap, 48px)",
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <Box
          data-name="Sidebar Tool Select Slot"
          sx={{
            flexShrink: 0,
            height: expanded ? "56px" : "var(--sidebar-brand-compact-size, 32.4px)",
            maxWidth: "100%",
            overflow: "hidden",
            transition: "height 220ms ease, width 220ms ease",
            width: expanded
              ? "min(215px, var(--sidebar-item-width, 277px))"
              : "var(--sidebar-brand-symbol-w, 49.68px)",
          }}
        >
          <Box
            sx={{
              opacity: selectVisible ? 1 : 0,
              pointerEvents: selectVisible ? "auto" : "none",
              transform: selectVisible ? "translateX(0)" : "translateX(-8px)",
              transition: "opacity 140ms ease, transform 220ms ease",
              visibility: expanded ? "visible" : "hidden",
              width: "min(215px, var(--sidebar-item-width, 277px))",
            }}
          >
            <SelectContent interactive={selectVisible} sidebarDark={sidebarDark} />
          </Box>
        </Box>

        <Box
          data-name="Nav List"
          sx={{
            alignItems: "flex-start",
            display: "flex",
            flexDirection: "column",
            gap: "var(--sidebar-nav-gap, 16px)",
            minHeight: 0,
            overflowY: "auto",
            pr: expanded ? 0.5 : 0,
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": {
              display: "none",
            },
          }}
        >
          {navItems.map((item) => (
            <NavButton
              key={item.id}
              active={item.id === activeItemId}
              badge={item.badge}
              expanded={expanded}
              labelVisible={labelVisible}
              label={item.label}
              iconScale={item.iconScale}
              src={item.icon}
              onClick={() => handleNav(item)}
            />
          ))}
        </Box>
      </Box>

      <Box
        data-name="Logout + Toggle"
        sx={{
          alignItems: "flex-start",
          display: "flex",
          flexDirection: "column",
          flexShrink: 0,
          gap: "var(--sidebar-bottom-gap, 24px)",
          overflow: "hidden",
          width: expanded
            ? "var(--sidebar-item-width, 277px)"
            : "var(--sidebar-collapsed-item-width, 48px)",
        }}
      >
        <DarkModeRow
          expanded={expanded}
          hidden={!SIDEBAR_COLOR_MODE.toggleEnabled}
          labelVisible={labelVisible}
          onToggleDarkMode={handleToggleSidebarDarkMode}
          sidebarDark={sidebarDark}
        />
        <NavButton
          expanded={expanded}
          labelVisible={labelVisible}
          label="Logout"
          src={logoutIcon}
          onClick={handleLogout}
          variant="logout"
        />
      </Box>
    </Box>
  );
}
